/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

window.Theme = window.Theme || {};

Theme.config = {
  mediaQueryMobile: window.matchMedia("screen and (max-width: 740px)").matches,
  mediaQueryTablet: window.matchMedia("screen and (max-width: 999px)").matches,
  get motionReduced() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  },
  isTouch: "ontouchstart" in window || navigator.maxTouchPoints > 0,
};

Theme.utils = {
  accordionHandler: {
    groups: [
      ".faq__item",
      ".product-facet__filter-item",
      '.mobile-nav__item[data-level="1"]',
      '.mobile-nav__item[data-level="2"]',
    ],

    init() {
      this.groups.forEach((selector) => {
        const items = [...document.querySelectorAll(selector)];
        if (items.length > 1) this._bindEvents(items);
      });
    },

    _bindEvents(items) {
      items.forEach((item) => {
        item.querySelector("button")?.addEventListener("click", () => {
          this._closeOthers(items, item);
        });
      });
    },

    _closeOthers(items, clickedItem) {
      items.forEach((item) => {
        if (item === clickedItem) return;
        item.querySelector("button")?.setAttribute("aria-expanded", "false");
        item.querySelector("collapsible-content")?.removeAttribute("open");
      });
    },
  },
  externalLinkHandler: {
    links: [],
    init() {
      this.links = [...document.querySelectorAll("a:not(.cookie-policy)")];
      this.links.forEach((link) => {
        if (link.hostname && link.hostname !== window.location.hostname) {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noreferrer noopener");
          link.setAttribute(
            "aria-label",
            `${link.textContent} (opens in new tab)`,
          );
        }
      });
    },
  },
  featCollectionButtonHandler: {
    init() {
      const sections = document.querySelectorAll(
        ".shopify-section--featured-collections",
      );

      sections.forEach((section) => {
        const button = section.querySelector("#active-tab-button");
        if (!button) return;

        section.addEventListener("click", (e) => {
          const tab = e.target.closest(".tabs-nav__item");
          if (!tab) return;

          const { buttonText, buttonUrl } = tab.dataset;
          const show = Boolean(buttonText && buttonUrl);

          if (show) {
            button.textContent = buttonText;
            button.href = buttonUrl;
          }
          button.style.display = show ? "inline-block" : "none";
        });
      });
    },
  },
  jumpLinkHandler: {
    focusableSelector: "a[href], button, input, select, textarea, [tabindex]",

    init() {
      // Brand Directory: "Back to Top" links
      document
        .querySelectorAll('.directory-top--wrapper a[href="#top"]')
        .forEach((link) => {
          link.addEventListener("click", (e) => this._goToTop(e));
          // role="button" means users expect Space to work
          link.addEventListener("keydown", (e) => {
            if (e.key === " ") this._goToTop(e);
          });
        });

      // FAQ: category navigation links
      document
        .querySelectorAll(".faq-navigation .scroll-spy__anchor")
        .forEach((link) => {
          link.addEventListener("click", () => this._focusHashTarget(link));
        });
    },

    _goToTop(e) {
      e.preventDefault();

      const target =
        document.getElementById("top") ||
        document.querySelector("main h1") ||
        document.querySelector("main") ||
        document.body;

      window.scrollTo({
        top: 0,
        behavior: Theme.config.motionReduced ? "auto" : "smooth",
      });
      this._focus(target);
    },

    _focusHashTarget(link) {
      // href="#category-…" points at the category's <h2>
      const target = document.getElementById(
        decodeURIComponent(link.hash.slice(1)),
      );
      if (target) this._focus(target);
    },

    _focus(target) {
      if (!target.matches(this.focusableSelector)) {
        target.setAttribute("tabindex", "-1");
        target.addEventListener(
          "blur",
          () => target.removeAttribute("tabindex"),
          { once: true },
        );
      }
      target.focus({ preventScroll: true });
    },
  },
  navHeightHandler: {
    init() {
      this.elements = {
        nav: document.querySelector("store-header"),
        announcement_bar: document.querySelector("announcement-bar"),
        pre_footer: document.querySelector(".pre-footer"),
      };
      this.setVariableStr();
    },
    setVariableStr() {
      for (const [key, el] of Object.entries(this.elements)) {
        const height = el?.offsetHeight || 0;
        const name = key.replaceAll("_", "-");
        document.body.style.setProperty(`--${name}-height`, `${height}px`);
      }
    },
  },
  scrollToTopHandler: {
    init() {
      this.scrollButton = document.getElementById("scroll-up");
      this.scrollVal = 300;

      if (this.scrollButton) {
        window.addEventListener("scroll", () => this._changeDisplay());
        this._scrollUp();
      }
    },

    _scrollUp() {
      this.scrollButton.addEventListener("click", (e) => {
        window.scrollTo({
          top: 0,
          left: 0,
        });
      });
    },

    _changeDisplay() {
      this.scrollButton.style.display =
        document.body.scrollTop > this.scrollVal ||
        document.documentElement.scrollTop > this.scrollVal
          ? "block"
          : "none";
    },
  },
  stickyFooterHandler: {
    scrollVal: 20,
    hiddenClass: "hidden",
    stickyClass: "sticky--footer",

    init() {
      if (!window.matchMedia(Theme.config.mediaQueryMobile).matches) return;

      this.footer = document.querySelector("footer.footer");
      this._toggleFooterOnScroll();
      this._bindDrawerTriggers();
    },

    _toggleFooterOnScroll() {
      if (!this.footer) return;

      const update = () => {
        this.footer.classList.toggle(
          this.hiddenClass,
          window.scrollY <= this.scrollVal,
        );
      };

      update();
      window.addEventListener("scroll", update, { passive: true });
    },

    _bindDrawerTriggers() {
      const openTriggers = [
        ".product-facet__meta-bar-item--filter",
        ".account--addresses button.account__address",
      ];
      const closeTriggers = [
        "facet-filters .drawer__header",
        "facet-filters .drawer__overlay",
        "drawer-content .drawer__header .drawer__close-button",
        "drawer-content .drawer__overlay",
      ];

      openTriggers.forEach((selector) => {
        document.querySelector(selector)?.addEventListener("click", () => {
          document.body.classList.remove(this.stickyClass);
        });
      });

      closeTriggers.forEach((selector) => {
        const trigger = document.querySelector(selector);
        trigger?.addEventListener("click", () => {
          setTimeout(() => {
            document.body.classList.add(this.stickyClass);
          }, this._getTransitionMs(trigger.parentNode));
        });
      });
    },

    _getTransitionMs(el) {
      // transitionDuration can be "0.3s", "300ms" or a list like "0.3s, 0.5s"
      const durations = getComputedStyle(el).transitionDuration.split(",");
      return Math.max(
        ...durations.map((d) => {
          const value = parseFloat(d) || 0;
          return d.trim().endsWith("ms") ? value : value * 1000;
        }),
      );
    },
  },
};

(function () {
  Theme.utils.accordionHandler.init();
  Theme.utils.externalLinkHandler.init();
  Theme.utils.featCollectionButtonHandler.init();
  Theme.utils.jumpLinkHandler.init();
  Theme.utils.navHeightHandler.init();
  Theme.utils.scrollToTopHandler.init();
  Theme.utils.stickyFooterHandler.init();
})();

class ProductImageGallery extends HTMLElement {
  connectedCallback() {
    if (this.swiper) return; // guard against double init if the element is moved
    this.elements = this._getElements();
    this._initSwiper();
    this._initLightGallery();
    // this._observeWrapper();
  }

  disconnectedCallback() {
    this.swiper?.destroy(true, true);
    this.lightGallery?.destroy();
    this.observer?.disconnect();
    this.swiper = this.lightGallery = this.observer = null;
  }

  _getElements() {
    return {
      scrollBar: this.querySelector(".product_detail__images_scrollbar"),
      prev: this.querySelector(".product_detail__images_control.prev"),
      next: this.querySelector(".product_detail__images_control.next"),
      lightboxLinks: [...this.querySelectorAll(".product_detail__image-link")],
    };
  }

  _initSwiper() {
    const centerImages = this.dataset.centerImages === "true";

    this.swiper = new Swiper(this, {
      grabCursor: true,
      slidesPerView: 1,
      spaceBetween: 10,
      breakpoints: {
        899: {
          slidesPerView: 3.75,
          spaceBetween: 10,
        },
      },
      mousewheel: centerImages ? false : { enabled: true, sensitivity: 4 },
      scrollbar: {
        el: this.elements.scrollBar,
        draggable: true,
      },
      navigation: {
        nextEl: this.elements.next,
        prevEl: this.elements.prev,
      },
    });
  }

  _initLightGallery() {
    this.lightGallery = lightGallery(this, {
      licenseKey: "B6CDB2EB-430F-4A16-B1B6-72A05EF0EAF5",
      plugins: [lgZoom, lgThumbnail],
      addClass: "custom-product-lightbox",
      mode: "lg-fade",
      speed: 500,
      counter: false,
      download: false,
      allowMediaOverlap: true,
      thumbnail: true,
      alignThumbnails: "right",
      thumbMargin: 0,
      thumbHeight: "120px",
      showZoomInOutIcons: false,
      actualSize: false,
      selector: this.elements.lightboxLinks,
      hideScrollbar: true,
    });

    this.addEventListener("lgBeforeOpen", () => {
      this.lastFocusedElement = this.contains(document.activeElement)
        ? document.activeElement
        : this.elements.lightboxLinks[this.lightGallery.index];
    });

    this.addEventListener("lgAfterClose", () => {
      this.lastFocusedElement?.focus({ preventScroll: true });
      this.lastFocusedElement = null;
      this.galleryOpen = false;
    });

    if (this.dataset.galleryLabel) {
      this.lightGallery.$container
        .get()
        .setAttribute("aria-label", this.dataset.galleryLabel);
    }

    this.addEventListener("lgAfterOpen", () => {
      this.galleryOpen = true;
      this._labelThumbnails();
    });
    this.addEventListener("lgAfterSlide", (event) =>
      this._announceSlide(event.detail.index),
    );
  }

  _labelThumbnails() {
    this.lightGallery.$container
      .get()
      .querySelectorAll(".lg-thumb-item img")
      .forEach((img) => {
        const link =
          this.elements.lightboxLinks[parseInt(img.dataset.lgItemId, 10)];
        img.setAttribute("alt", link?.getAttribute("alt") || "");
      });
  }

  _announceSlide(index) {
    const template = window.themeVariables?.strings?.imagePosition;
    if (!this.galleryOpen || !template || typeof announceStatus !== "function")
      return;

    const alt = this.elements.lightboxLinks[index]?.getAttribute("alt");
    const position = template
      .replace("[index]", index + 1)
      .replace("[count]", this.elements.lightboxLinks.length);
    const message = alt ? `${position}, ${alt}` : position;

    announceStatus(message);
  }

  _observeWrapper() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.parentNode.classList.toggle(
            "-scrolled",
            !entry.isIntersecting,
          );
        });
      },
      { rootMargin: "-100% 0px 0px 0px", threshold: 0 },
    );

    this.observer.observe(this);
    const product = document.querySelector(".product");
    if (product) this.observer.observe(product);
  }
}

if (!customElements.get("custom-product-media")) {
  customElements.define("custom-product-media", ProductImageGallery);
}

class LandingPageApp {
  constructor() {
    this.decadeSections = [
      ...document.querySelectorAll(".section--decades > .decade-wrapper"),
    ];
    this.decadeReveals = new Map();
    this.nav = document.querySelector("store-header.header");

    this._init();
  }

  _init() {
    if (this.decadeSections.length) {
      this._createLenis();
      this._renderLenis();
      gsap.registerPlugin(ScrollTrigger, Flip);
      this._createHero();
      this._animateFiveDecades();
      this._resetYearLinkBehavior();
      this._revealOnKeyboardFocus();

      if (this.decadeSections.length) {
        this.decadeSections.forEach((section, i) => {
          this._createDecade(section, i);
        });
      }
      console.log("Animations have been initialized");
    }
  }

  _createLenis() {
    this.lenis = new Lenis({
      lerp: 0.05,
      easing: (t) => 1 - Math.pow(1 - t, 5),
    });
  }

  _renderLenis(time) {
    this.lenis.raf(time);
    requestAnimationFrame(this._renderLenis.bind(this));
  }

  _getElements(section) {
    if (section === "hero") {
      return {
        mainWrap: document.querySelector(".editorial_hero__video"),
        flipWrap: document.getElementById("hero_video_flip_container"),
        contentWrap: document.querySelector(".editorial_hero__content"),
        content: document.querySelector(".hero_content"),
        fiveDecadeContent: document.querySelector(
          ".editorial_hero__decade_wrapper",
        ),
        years: document.querySelector(".hero_years"),
        video: document.getElementById("hero_video_flip_element"),
      };
    } else {
      return {
        horScroll: section.querySelector(".hor-scroll"),
        horScrollWrap: section.querySelector(".hor-scroll-wrapper"),
        title: section.querySelector(".title-wrapper"),
        content: section.querySelector(".content-wrapper"),
      };
    }
  }

  _setInitState(section) {
    const { horScroll, title, content } = this._getElements(section);

    gsap.set(horScroll, {
      x: window.innerWidth,
    });

    gsap.set(title, {
      yPercent: -150,
      opacity: 0,
    });

    gsap.set(content, {
      opacity: 0,
      xPercent: 20,
      clipPath: "00 ",
    });

    gsap.set(section, {
      opacity: 1,
    });
  }

  _revealOnKeyboardFocus() {
    document.addEventListener("focusin", (event) => {
      const target = event.target;
      if (!target.matches(":focus-visible")) return;

      let scrollY = null;
      if (target.closest(".hero_content")) {
        scrollY = this.heroContentReveal?.scrollTrigger?.start + 1;
      } else if (target.closest(".editorial_hero__decade_wrapper")) {
        scrollY = this.fiveDecadesReveal?.scrollTrigger?.start + 1;
      } else {
        const reveal = this.decadeReveals.get(target.closest(".decade-wrapper"));
        if (reveal) {
          const { start, end } = reveal.scrollTrigger;
          scrollY = start + (end - start) * reveal.progress;
        }
      }

      if (Number.isFinite(scrollY)) {
        this.lenis.scrollTo(scrollY, { immediate: true, force: true });
      }

      const nudgeIntoView = (attempts) => {
        const overflow =
          target.getBoundingClientRect().bottom - window.innerHeight + 16;
        if (overflow <= 0 || attempts === 0) return;

        this.lenis.scrollTo(window.scrollY + overflow, {
          immediate: true,
          force: true,
        });
        requestAnimationFrame(() => nudgeIntoView(attempts - 1));
      };
      requestAnimationFrame(() => nudgeIntoView(5));
      setTimeout(() => nudgeIntoView(5), 1100);
    });
  }

  _resetYearLinkBehavior() {
    const { years: yearsWrap } = this._getElements("hero");
    const years = [...yearsWrap.querySelectorAll(".year")];

    years.forEach((link) => {
      link.addEventListener("click", function (e) {
        const targetId = this.getAttribute("href");
        const target = document.getElementById(targetId);

        if (target) {
          e.preventDefault();
          history.pushState(null, null, targetId);
          target.scrollIntoView({ behavior: "auto" });
        }
      });
    });
  }

  _animateFiveDecades() {
    const navHeight = this.nav.offsetHeight;
    const heroDecadeSection = document.querySelector(
      ".editorial_hero__decades",
    );
    const downArrow = document.querySelector(".scroll_down .icon-custom.arrow");
    const decadeNav = this.nav.querySelector(".decade__nav.mobile");

    const { fiveDecadeContent } = this._getElements("hero");
    const fiveDecadeElements = fiveDecadeContent.children;

    gsap.set(fiveDecadeElements, {
      opacity: 0,
      xPercent: -10,
    });

    this.fiveDecadesReveal = gsap.to(fiveDecadeElements, {
      xPercent: 0,
      opacity: 1,
      stagger: {
        each: 0.05,
        from: "end",
      },
      scrollTrigger: {
        trigger: heroDecadeSection,
        start: `top top+=${navHeight}`,
        toggleActions: "play none none reverse",
        onEnter: () => {
          decadeNav.classList.remove("inactive");
        },
        onLeaveBack: () => {
          decadeNav.classList.add("inactive");
        },
      },
    });

    gsap.to(downArrow, {
      fill: "var(--color-neutral)",
      scrollTrigger: {
        trigger: heroDecadeSection,
        start: "top bottom",
        end: "bottom 40%",
        toggleActions: "play reset play reset",
      },
    });
  }

  _createHero() {
    const elements = this._getElements("hero");
    const navHeight = this.nav.offsetHeight;

    const missingElements = Object.entries(elements).filter(([key, el]) => !el);
    if (missingElements.length) {
      console.log(
        "Missing elements:",
        missingElements.map(([key]) => key),
      );
      return;
    }
    if (Object.values(elements).some((el) => !el)) return;

    this._buildHeroFlip(elements);

    let resizeTimer;
    let lastSize = [window.innerWidth, window.innerHeight];
    const rebuild = () => {
      lastSize = [window.innerWidth, window.innerHeight];
      this._buildHeroFlip(elements);
      ScrollTrigger.refresh();
    };
    window.addEventListener("resize", () => {
      const [lastWidth, lastHeight] = lastSize;
      const isToolbarResize =
        window.matchMedia("(pointer: coarse)").matches &&
        window.innerWidth === lastWidth &&
        Math.abs(window.innerHeight - lastHeight) < 150;
      if (isToolbarResize) return;

      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(rebuild, 250);
    });
    if (document.readyState !== "complete") {
      window.addEventListener("load", rebuild, { once: true });
    }
  }

  _buildHeroFlip({ mainWrap, flipWrap, contentWrap, content, video }) {
    const navHeight = this.nav.offsetHeight;
    const staticVideo = document.getElementById("hero_video_static_element");
    const decadeNav = this.nav.querySelector(".decade__nav.mobile");

    this.heroFlipCtx?.revert();
    video.classList.remove("hidden");
    staticVideo.classList.add("hidden");

    this.heroFlipCtx = gsap.context(() => {
      const contentElements = content.children;

      gsap.set(contentElements, {
        opacity: 0,
        xPercent: -10,
      });

      const endState = Flip.getState(flipWrap);
      const tweenMeetPoint = `clamp(top+=${navHeight} top+=${navHeight})`;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: mainWrap,
          start: "clamp(top 50%)",
          endTrigger: contentWrap,
          end: tweenMeetPoint,
          invalidateOnRefresh: true,
          scrub: true,
        },
      });

      // Flip animation
      tl.add(
        Flip.fit(video, endState, {
          ease: "none",
          duration: 1,
        }),
      );

      this.heroContentReveal = gsap.to(contentElements, {
        xPercent: Theme.config.mediaQueryMobile ? 10 : 0,
        opacity: 1,
        stagger: {
          each: 0.05,
          from: "end",
        },
        scrollTrigger: {
          trigger: contentWrap,
          start: tweenMeetPoint,
          invalidateOnRefresh: true,
          toggleActions: "play none none reverse",
          onEnter: () => {
            video.classList.add("hidden");
            staticVideo.classList.remove("hidden");
          },
          onLeaveBack: () => {
            video.classList.remove("hidden");
            staticVideo.classList.add("hidden");
            decadeNav?.classList.add("inactive");
          },
        },
      });
    });
  }

  _createDecade(section, i) {
    this._setInitState(section);
    this._animateDecadeContent(section, i);
  }

  _animateDecadeContent(section, i) {
    const { horScroll, title, content } = this._getElements(section);
    const navHeight = this.nav.offsetHeight;
    const delay = 500;

    setTimeout(() => {
      this._startAnimation(horScroll, title, content, navHeight, section, i);
    }, delay);
  }

  _startAnimation(horScroll, title, content, navHeight, section, i) {
    const scrollWidth = -(horScroll.offsetWidth - window.innerWidth - 100);
    const duration = -(scrollWidth / 100);
    const isLastSection = this.decadeSections.length - 1 === i;
    const scrollDownArrow = document.querySelector(
      ".scroll_arrow__wrapper.scroll_down",
    );

    const mainTl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: `top top+=${navHeight}`,
        end: `top+=${section.offsetHeight + 2000}px`,
        scrub: 0.5,
        pin: true,
        onEnterBack: () => {
          if (isLastSection) {
            scrollDownArrow.classList.remove("hidden");
          }
        },
        onLeave: () => {
          if (isLastSection) {
            scrollDownArrow.classList.add("hidden");
          }
        },
      },
      defaults: {
        duration: 2,
        ease: "power4.out",
      },
    });

    mainTl
      .to(title, {
        opacity: 1,
      })
      .to(title, {
        yPercent: 0,
      })
      .to(
        content,
        {
          xPercent: 0,
          opacity: 1,
        },
        "<50%",
      );

    this.decadeReveals.set(section, {
      scrollTrigger: mainTl.scrollTrigger,
      progress: mainTl.recent().endTime() / mainTl.duration(),
    });

    mainTl
      .to(
        horScroll,
        {
          x: scrollWidth,
          ease: "none",
          duration: duration,
        },
        "<0%",
      );
  }
}

document.addEventListener("DOMContentLoaded", () => {
  // ----- LANDING PAGE ANIMATIONS
  new LandingPageApp();
});

(() => {
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  document
    .querySelectorAll('[data-action="toggle-videos"]')
    .forEach((button) => {
      const group = button.closest("[data-video-group]");
      if (!group) return;

      const setPaused = (paused, announce) => {
        group.querySelectorAll("video").forEach((video) => {
          if (paused) {
            video.autoplay = false;
            video.pause();
          } else {
            video.play().catch(() => {});
          }
        });

        button.dataset.paused = paused;
        button.querySelector(".video-toggle__label").textContent = paused
          ? button.dataset.labelPlay
          : button.dataset.labelPause;
        button
          .querySelector(".video-toggle__icon--pause")
          .toggleAttribute("hidden", paused);
        button
          .querySelector(".video-toggle__icon--play")
          .toggleAttribute("hidden", !paused);

        if (announce && typeof announceStatus === "function") {
          const strings = window.themeVariables?.strings || {};
          announceStatus(paused ? strings.paused : strings.playing);
        }
      };

      button.addEventListener("click", () =>
        setPaused(button.dataset.paused !== "true", true),
      );

      if (prefersReducedMotion) setPaused(true, false);
    });
})();
